import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { ChevronRightIcon, IconButton, PopOutIcon, Popover, tokens } from '@treams/treams-ui';

export type OverviewTone = 'info' | 'warning' | 'success' | 'brand';

const toneTokens = {
	info: tokens.info,
	warning: tokens.warning,
	success: tokens.success,
	brand: tokens.brand,
};

const Tile = styled.div<{ $interactive?: boolean }>`
	display: flex;
	flex-direction: column;
	overflow: hidden;
	border-radius: ${tokens.radius.md}px;
	background: ${tokens.surface.white};
	box-shadow: ${tokens.shadow.xs};
	text-decoration: none;
	color: inherit;

	${(props) =>
		props.$interactive &&
		`
		cursor: pointer;

		&:hover {
			box-shadow: ${tokens.shadow.md};
		}
	`}
`;

const TileLink = styled(Link)`
	display: block;
	text-decoration: none;
	color: inherit;
`;

const TileAnchor = styled.a`
	display: block;
	text-decoration: none;
	color: inherit;
`;

const Header = styled.div<{ $tone: OverviewTone }>`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 112px;
	padding: ${tokens.padding.section}px;
	background: ${(props) => toneTokens[props.$tone].background};
	border: 1px solid ${(props) => toneTokens[props.$tone].border};
	border-radius: ${tokens.radius.md}px ${tokens.radius.md}px 0 0;
	color: ${(props) => toneTokens[props.$tone].iconOnBg};

	svg {
		display: block;
		width: 44px;
		height: 44px;
		color: inherit;
	}
`;

const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${tokens.gap.element}px;
	padding: ${tokens.padding.control}px ${tokens.padding.container}px;
	border: 1px solid ${tokens.border.subtle};
	border-top: 0;
	border-radius: 0 0 ${tokens.radius.md}px ${tokens.radius.md}px;
	background: ${tokens.surface.white};
`;

const Label = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: ${tokens.typography.weights.medium};
	line-height: ${tokens.typography.lineHeights.heading};
	color: ${tokens.text.primary};
`;

export default function OverviewItem(props: {
	tone: OverviewTone;
	icon: ReactNode;
	title: string;
	link: string;
	enabled?: boolean;
	upsell?: ReactNode;
}) {
	const [visible, setVisible] = useState(false);
	const disabled = props.enabled === false;
	const external = props.link.startsWith('http');
	const interactive = !disabled || Boolean(props.upsell);

	const tile = (
		<Tile
			$interactive={interactive}
			onClick={disabled && props.upsell ? () => setVisible(true) : undefined}
		>
			<Header $tone={props.tone}>{props.icon}</Header>
			<Footer>
				<Label title={props.title}>{props.title}</Label>
				<div style={{ pointerEvents: 'none' }}>
					<IconButton type="secondary" icon={external ? <PopOutIcon /> : <ChevronRightIcon />} />
				</div>
			</Footer>
		</Tile>
	);

	return (
		<Popover.Target
			center
			visible={visible}
			contents={props.upsell}
			onDismiss={() => setVisible(false)}
		>
			{disabled ? (
				tile
			) : external ? (
				<TileAnchor href={props.link} target="_blank" rel="noreferrer">
					{tile}
				</TileAnchor>
			) : (
				<TileLink to={props.link}>{tile}</TileLink>
			)}
		</Popover.Target>
	);
}
